import type { CSSProperties } from 'react'
import { type HTMLAttributes, forwardRef } from 'react'

import { type VariantProps, cva } from 'class-variance-authority'

import { classNames } from '@/utils/helpers.ts'

export type BadgeVariantsType = VariantProps<typeof badgeVariants>

export const badgeVariants = cva(
  'inline-flex items-center rounded-full px-3 h-[32px] text-sm font-medium text-secondary-700 ',
  {
    variants: {
      variant: {
        success: 'bg-teal-500/10 dark:bg-teal-500/10 text-text-default',
        danger: 'bg-danger-light dark:bg-danger-default/30 text-text-default',
        warning:
          'bg-warning-light/10 dark:bg-warning-default/10 text-text-default',
        neutral:
          'bg-primary-50 dark:bg-primary-500/50 text-text-default dark:text-white',
      },
      dot: {
        success:
          "before:flex-shrink-0 before:content-[''] before:w-[6px] before:h-[6px] before:bg-teal-500 dark:before:bg-teal-500 before:mr-2 before:rounded-full",
        danger:
          "before:flex-shrink-0 before:content-[''] before:w-[6px] before:h-[6px] before:bg-danger-default dark:before:bg-danger-default before:mr-2 before:rounded-full",
        warning:
          "before:flex-shrink-0 before:content-[''] before:animate-pulse before:w-[6px] before:h-[6px] before:bg-warning-default dark:before:bg-warning-default before:mr-2 before:rounded-full",
        neutral:
          "before:flex-shrink-0 before:content-[''] before:w-[6px] before:h-[6px] before:bg-primary-500 dark:before:bg-primary-light before:mr-2 before:rounded-full",
        none: '',
      },
    },
    defaultVariants: {
      variant: 'success',
      dot: 'none',
    },
  },
)

interface LabelBaseProps {
  withDot?: boolean
}

export type LabelProps = LabelBaseProps &
  HTMLAttributes<HTMLDivElement> &
  BadgeVariantsType & {
    style?: CSSProperties
  }

export const Tag = forwardRef<HTMLDivElement, LabelProps>(
  ({ className, variant, withDot, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={classNames(
          badgeVariants({ variant, dot: withDot ? variant : 'none' }),
          className,
        )}
        {...props}
      />
    )
  },
)
Tag.displayName = 'Tag'
